import { useCallback, useEffect, useRef, useState } from 'react';
import { useBoolean, useEvent, usePromise } from 'react-use';

import { localStorageService } from '../services/localStorageService';

type AsyncDispatch<A> = (value: A) => Promise<void>;

type SetStateAction<S> = (prevState: S) => S;

export type UseThreadSafeLocalStorageReturn<T> = [boolean, T | undefined, AsyncDispatch<SetStateAction<T | undefined>>, () => void];

export const getLocalStorageLock = (key: string) => `localStorage:${key}`;

const threadSafeInvoke = async <R>(lock: string, callback: () => R | Promise<R>): Promise<R> => {
  return new Promise(resolve => {
    const _callback = async () => {
      const result = await callback();
      resolve(result);
    };

    if ('locks' in navigator) {
      navigator.locks.request(lock, _callback);
    } else {
      _callback();
    }
  });
};

const getValueFromLocalStorage = <T>(key: string, initialValue?: T): T | undefined => {
  const value = localStorageService?.getItem(key);
  return value ? JSON.parse(value) : initialValue;
};

interface ThreadSafeUpdateItemOptions<T> {
  initialValue?: T;
}

export const threadSafeUpdateItem = async <T>(
  key: string,
  getNextState: SetStateAction<T | undefined>,
  options?: ThreadSafeUpdateItemOptions<T>
) => {
  const lock = getLocalStorageLock(key);
  return await threadSafeInvoke(lock, () => {
    const prevState = getValueFromLocalStorage<T>(key, options?.initialValue);
    const nextState = getNextState(prevState);
    localStorageService?.setItem(key, JSON.stringify(nextState));
  });
};

// Because local storage can be updated in anywhere, and is not thread-safe.
// In BRAINTECH-1981, we need to store the last timestamp user visited each learn page.
// So if user open two learn pages in new tab, the data could be lost. And we need to
// create a mutex lock to prevent writing/ overwriting value for a specific key at the same time.
export const useThreadSafeLocalStorage = <T>(key: string, initialValue?: T): UseThreadSafeLocalStorageReturn<T> => {
  const resolveOnlyMounted = usePromise();

  const getLock = useRef((key: string) => `localStorage:${key}`);

  const initializer = useRef(async (key: string, initialValue?: T): Promise<T | undefined> => {
    const lock = getLock.current(key);
    return await threadSafeInvoke(lock, () => {
      const initialState = getValueFromLocalStorage<T>(key, initialValue);
      if (initialState === initialValue) {
        localStorageService?.setItem(key, JSON.stringify(initialValue));
      }
      return initialState;
    });
  });

  const [isInitialized, setIsInitialized] = useBoolean(false);

  const [state, setState] = useState<T | undefined>(undefined);

  // Re-initialize state whenever key changed
  useEffect(() => {
    (async () => {
      setIsInitialized(false);
      const initialState = await resolveOnlyMounted(initializer.current(key, initialValue));
      setState(initialState);
      setIsInitialized(true);
    })();
  }, [key, initialValue, resolveOnlyMounted, setIsInitialized, setState]);

  const update: AsyncDispatch<SetStateAction<T | undefined>> = useCallback(
    async getNewState => {
      const lock = getLock.current(key);
      const threadSafeSetItem = threadSafeInvoke(lock, () => {
        const prevState = getValueFromLocalStorage<T>(key, initialValue);
        const newState = getNewState(prevState);
        const value = JSON.stringify(newState);
        localStorageService?.setItem(key, value);
        return newState;
      });
      const newState = await resolveOnlyMounted(threadSafeSetItem);
      setState(newState);
    },
    [key, resolveOnlyMounted, setState]
  );

  const remove = useCallback(async () => {
    const lock = getLock.current(key);
    const threadSafeRemoveItem = threadSafeInvoke(lock, () => localStorageService?.removeItem(key));
    await resolveOnlyMounted(threadSafeRemoveItem);
    setState(undefined);
  }, [key, resolveOnlyMounted, setState]);

  const onLocalStorageUpdated = useCallback(
    (event: StorageEvent) => {
      if (event.key !== key) return;
      setState(event.newValue !== null ? JSON.parse(event.newValue) : undefined);
    },
    [key, setState]
  );

  // Listen on local storage update event to sync the latest version
  useEvent('storage', onLocalStorageUpdated);

  return [isInitialized, state, update, remove];
};
